// src/components/workshop-hub/WorkshopsTab.tsx
import { useCallback, useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  IconButton,
  Paper,
  CircularProgress,
  Pagination,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Search as SearchIcon,
  GridView as GridViewIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { apiRemote } from "@/api";
import useDebounce from "@/hooks/use-debounce";
import { useUser } from "@/contexts/UserContext"; // Import useUser để lấy thông tin is_registered

export default function WorkshopsTab() {
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "calendar">(() => {
    if (typeof window !== "undefined") {
      const savedViewMode = sessionStorage.getItem("workshopViewMode");
      return savedViewMode === "calendar" ? "calendar" : "grid";
    }
    return "grid";
  });
  const { user } = useUser(); // Lấy user từ context
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [workshops, setWorkshops] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(6);
  const [totalPages, setTotalPages] = useState<number>(1);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [showMyWorkshops, setShowMyWorkshops] = useState<boolean>(false);
  const [showRegisteredWorkshops, setShowRegisteredWorkshops] = useState<boolean>(false);

  const loadWorkshop = useCallback(async () => {
    setLoading(true);
    apiRemote.getListWorkshop(
      currentPage,
      itemsPerPage,
      searchTerm,
      selectedCategory === "all" ? undefined : selectedCategory,
      selectedType === "all" ? undefined : selectedType,
      selectedStatus === "all" ? undefined : selectedStatus,
      user?.user_login,
      showMyWorkshops,
      showRegisteredWorkshops,
      userTimeZone
    ).then((data)=>{
      if (data.data.data && Array.isArray(data.data.data)) {
        setWorkshops(data.data.data);
        setTotalPages(data.data.last_page);
      } else {
        setWorkshops([]);
        setTotalPages(1);
      }
    }).catch((e)=>{
      console.error("Error:", e);
      setWorkshops([]);
      setTotalPages(1);
    }).finally(()=>{
      setLoading(false);
    });
  }, [currentPage, itemsPerPage, debouncedSearchTerm, selectedCategory, selectedType, selectedStatus, user?.user_login, showMyWorkshops, showRegisteredWorkshops]);

  useEffect(() => {
    apiRemote
      .getListCategory()
      .then((data) => {
        setCategories(data.data || []);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
        setCategories([]);
      });
  }, []);

  useEffect(() => {
    loadWorkshop();
  }, [loadWorkshop]); // Chỉ chạy khi loadWorkshop thay đổi (do dependencies của nó thay đổi)

  useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("workshopViewMode", viewMode);
    }
  }, [viewMode]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleMyWorkshopsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowMyWorkshops(event.target.checked);
    if (event.target.checked) {
      // Nếu chọn "My workshops", bỏ chọn "My registered workshops"
      setShowRegisteredWorkshops(false);
    }
    setCurrentPage(1); // Reset trang về 1 khi thay đổi bộ lọc
  };

  // Hàm xử lý khi checkbox "My registered workshops" thay đổi
  const handleRegisteredWorkshopsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowRegisteredWorkshops(event.target.checked);
    if (event.target.checked) {
      // Nếu chọn "My registered workshops", bỏ chọn "My workshops"
      setShowMyWorkshops(false);
    }
    setCurrentPage(1); // Reset trang về 1 khi thay đổi bộ lọc
  };

  const processedWorkshops = workshops.map((workshop) => {
    let topics = [];
    try {
      topics = workshop.topic ? JSON.parse(workshop.topic).map((tag: any) => tag.name) : [];
    } catch (e) {
      console.error("Failed to parse topic:", workshop.topic, e);
      topics = [];
    }

    const formattedDate = new Date(workshop.date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    const calculatedStatus = workshop.status;
    const shouldShowRegistered = workshop.is_registered && calculatedStatus !== 'draft' && calculatedStatus !== 'cancelled';

    const processedHost = workshop.hosts && workshop.hosts !== "[]"
    ? JSON.parse(workshop.hosts)
      .map((host: any) => host.host_name)
      .join(", ")
    : workshop.host_name || "N/A";

    return {
      ...workshop,
      host: processedHost,
      image: workshop.image_url,
      topics: topics,
      date: formattedDate,
      is_registered: shouldShowRegistered, // Thêm is_registered
      status: workshop.status,
    };
  });

  const workshopsByDate = processedWorkshops.reduce(
    (acc: any, workshop: any) => {
      const date = workshop.date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(workshop);
      return acc;
    },
    {}
  );

  return (
    <Box>
      {/* Filter Bar (Giữ nguyên như code cũ) */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            fullWidth
            placeholder="Search for workshops, topics, or hosts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value as string)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={selectedType}
              label="Type"
              onChange={(e) => setSelectedType(e.target.value as string)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="upcoming">Upcoming</MenuItem>
              <MenuItem value="past">Past</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value as string)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="full">Full</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <Box
            sx={{
              display: "flex",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <IconButton
              onClick={() => setViewMode("grid")}
              color={viewMode === "grid" ? "primary" : "default"}
            >
              <GridViewIcon />
            </IconButton>
            <IconButton
              onClick={() => setViewMode("calendar")}
              color={viewMode === "calendar" ? "primary" : "default"}
            >
              <CalendarIcon />
            </IconButton>
          </Box>
        </Stack>
        {user?.user_login && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showMyWorkshops}
                  onChange={handleMyWorkshopsChange}
                  name="myWorkshops"
                  color="primary"
                />
              }
              label="My workshops"
            />

            {/* Checkbox cho "My registered workshops" */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={showRegisteredWorkshops}
                  onChange={handleRegisteredWorkshopsChange}
                  name="registeredWorkshops"
                  color="primary"
                />
              }
              label="My registered workshops"
            />
          </Box>
        )}
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
          <Typography variant="h6" color="text.secondary" sx={{ ml: 2 }}>Loading workshops...</Typography>
        </Box>
      ) : (
        <>
          {viewMode === "grid" ? (
            // Workshop Grid View
            <Grid container spacing={4}>
              {processedWorkshops.length > 0 ? (
                processedWorkshops.map((workshop) => (
                  <Grid item xs={12} sm={6} md={4} key={workshop.id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 12px 25px rgba(0,0,0,0.1)",
                        },
                         // Thay đổi màu viền nếu đã đăng ký
                        border: workshop.is_registered ? '2px solid' : '1px solid',
                        borderColor: workshop.is_registered ? 'success.main' : 'divider',
                        backgroundColor: workshop.is_registered ? 'success.light' : 'background.paper',
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="100%"
                        image={workshop.image}
                        alt={workshop.title}
                        sx={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, objectFit: "fill" }}
                      />
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" gutterBottom sx={{ 
                              mb: 0,
                              color: workshop.is_registered ? '#fff' : '#000',
                            }}>
                            {workshop.title}
                            </Typography>
                            {workshop.is_registered && (
                            <Chip
                                label="Registered"
                                color="success"
                                size="small"
                                sx={{ ml: 1, fontWeight: 'bold' }}
                            />
                            )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                          sx={{
                            color: workshop.is_registered ? '#fff' : '#000',
                          }}
                        >
                          Host {workshop.host}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 2, flexWrap: "wrap" }}
                        >
                          {workshop.topics.map((topic: string) => (
                            <Chip
                              key={topic}
                              label={topic}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mb: 1,
                                color: workshop.is_registered ? '#fff' : '#1976d2',
                              }}
                            />
                          ))}
                        </Stack>
                        <Typography
                          variant="body2"
                          sx={{ mb: 2, display: "flex", alignItems: "center",
                            color: workshop.is_registered ? '#fff' : '#000',
                          }}
                        >
                          📅 {workshop.date}
                        </Typography>
                        <Button
                          variant={workshop.is_registered ? "contained" : "outlined"}
                          color={workshop.is_registered ? "success" : "primary"}
                          sx={{ mt: "auto" }}
                          onClick={() => navigate(`./detail/${workshop.id}`)}
                          // disabled={workshop.is_registered} // Tùy chọn: vô hiệu hóa nút
                        >
                          View Details
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    align="center"
                    sx={{ mt: 4 }}
                  >
                    No workshops found matching your criteria.
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : (
            // Calendar View (Giữ nguyên như code cũ)
            <Box>
              <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                Workshops (Calendar View)
              </Typography>
              {Object.keys(workshopsByDate).length > 0 ? (
                Object.keys(workshopsByDate)
                  .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                  .map((date) => (
                    <Box key={date} sx={{ mb: 4 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 2,
                          borderBottom: 1,
                          borderColor: "divider",
                          pb: 1,
                        }}
                      >
                        🗓️ {date}
                      </Typography>
                      <Grid container spacing={2}>
                        {workshopsByDate[date].map((workshop: any) => (
                          <Grid item xs={12} sm={6} md={4} key={workshop.id}>
                            <Paper
                              elevation={2}
                              sx={{
                                p: 2,
                                display: "flex",
                                flexDirection: "column",
                                borderRadius: 2,
                                height: "100%",
                                transition:
                                  "transform 0.2s ease, box-shadow 0.2s ease",
                                "&:hover": {
                                  transform: "translateY(-4px)",
                                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                                },
                                cursor: "pointer",
                                // Thay đổi màu viền nếu đã đăng ký
                                border: workshop.is_registered ? '2px solid' : '1px solid',
                                borderColor: workshop.is_registered ? 'success.main' : 'divider',
                                backgroundColor: workshop.is_registered ? 'success.light' : 'background.paper',
                              }}
                              onClick={() => navigate(`./detail/${workshop.id}`)}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                                    {workshop.title}
                                    </Typography>
                                    {workshop.is_registered && (
                                    <Chip
                                        label="Registered"
                                        color="success"
                                        size="small"
                                        sx={{ ml: 1, fontWeight: 'bold' }}
                                    />
                                    )}
                                </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                Host {workshop.host}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mb: 1, flexWrap: "wrap" }}
                              >
                                {workshop.topics.map((topic: any) => (
                                  <Chip
                                    key={topic}
                                    label={topic}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Stack>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))
              ) : (
                <Typography
                  variant="h5"
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 4 }}
                >
                  No workshops found for the selected date range or filters.
                </Typography>
              )}
            </Box>
          )}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}